import React, { useCallback, useMemo } from "react";

import IndeterminateCheckBoxOutlinedIcon from "@mui/icons-material/IndeterminateCheckBoxOutlined";
import { Box, BoxProps, Checkbox, FormControlLabel, Stack, Typography } from "@mui/material";
import { FolderTypeIcon, VisibilityLock } from "assets/material-custom-icons";
import { FileIcon } from "components/old-data-box-components";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { IComponent } from "store/types";

import { useShareContext } from "../context/share-context";

interface IAccessThreeRowProps {
  component: IComponent;
  subIndex: number;
}

export const AccessThree: React.FC<IAccessThreeRowProps> = ({ component, subIndex }) => {
  const { stepKey, handleElementClick, state } = useShareContext();

  const isFullAccess = useMemo(() => {
    return !!state[stepKey]?.deal_id?.length;
  }, [state, stepKey]);

  const wholeData: IComponent[] = useMemo(() => {
    return [...component.children.filter((item) => item.type !== "section")];
  }, [component]);

  const sections = useMemo(() => {
    return [...component.children.filter((item) => item.type === "section")];
  }, [component]);

  const childrenStyles: BoxProps["sx"] = {
    display: "flex",
    gap: "8px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "180px",
    alignItems: "center"
  };

  const renderTitle = useCallback((item: IComponent) => {
    return (
      <Box sx={childrenStyles}>
        <FileIcon
          type={item?.file_type || item?.type}
          style={{ fill: item.isPrivate ? "#BAC7CE" : "#5D7782", fontSize: "16px" }}
        />
        <MaterialTooltip isShow title={item.title}>
          <Typography sx={{ width: "150px", whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
            {item.title}
          </Typography>
        </MaterialTooltip>
      </Box>
    );
  }, []);
  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        paddingLeft={subIndex === 1 ? "0px" : (subIndex - 1) * 35 + "px"}
      >
        <FormControlLabel
          sx={{ minHeight: "43px", mr: 0 }}
          disabled={isFullAccess || component.isPrivate}
          label={
            <Stack direction="row" gap="8px" maxWidth="180px">
              <FolderTypeIcon sx={{ width: "24px", fill: component.isPrivate ? "#BAC7CE" : "#5D7782" }} />
              <Typography whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" width="150px">
                {component.title}
              </Typography>
            </Stack>
          }
          control={
            <Checkbox
              checked={component.access === "full" || component.access === "partial" || isFullAccess}
              indeterminate={component.access === "partial"}
              indeterminateIcon={<IndeterminateCheckBoxOutlinedIcon />}
              onChange={() =>
                handleElementClick(component.id, component.type, component.holderId, state, component?.access as string)
              }
            />
          }
        />
        {component.isPrivate && <VisibilityLock sx={{ fill: "#BAC7CE" }} />}
      </Stack>
      {wholeData?.map((item) => {
        return (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            key={item.id}
            paddingLeft={subIndex === 1 ? "35px" : subIndex * 35 + "px"}
          >
            <FormControlLabel
              sx={{ minHeight: "43px", mr: 0 }}
              disabled={isFullAccess || item.isPrivate}
              label={renderTitle(item)}
              control={
                <Checkbox
                  checked={item.access === "full" || item.access === "partial" || (isFullAccess && !item.isPrivate)}
                  onChange={() => {
                    handleElementClick(item.id, item.type, item.holderId, state, item?.access as string);
                  }}
                />
              }
            />
            {item.isPrivate && <VisibilityLock sx={{ fill: "#BAC7CE" }} />}
          </Stack>
        );
      })}
      {sections?.map((component) => <AccessThree key={component.id} component={component} subIndex={subIndex + 1} />)}
    </>
  );
};
